import {
  CONTRACT_ERROR_INTERFACE,
  KNOWN_REVERT_SELECTORS,
} from "./errors.constants";
import {
  formatDecodedContractError,
  formatSignedTokenAmount,
  formatTokenAmount,
  humanizeErrorName,
} from "./errors.format";

export const formatParsedContractError = (data: string): string | undefined => {
  let parsed: ReturnType<typeof CONTRACT_ERROR_INTERFACE.parseError>;
  try {
    parsed = CONTRACT_ERROR_INTERFACE.parseError(data);
  } catch {
    // Selector/args don't match any signature we know — let the raw decoder
    // turn it into a clean error-code block instead.
    return undefined;
  }

  const selector = data.slice(0, 10).toLowerCase();

  // The signature list is ABI-canonical (no parameter names), so arguments are
  // only addressable positionally — ethers leaves named accessors undefined.
  try {
    const { signature, name, args } = parsed;

    if (signature === "InsufficientBalance(uint256,uint256)") {
      return formatDecodedContractError(
        `Insufficient balance: requires ${formatTokenAmount(args[0])} USDC, available ${formatTokenAmount(args[1])} USDC.`,
        parsed,
        data
      );
    }

    if (signature === "InsufficientBalance(address,uint256,uint256,uint256)") {
      return formatDecodedContractError(
        `Insufficient balance for ${args[0]}: requires ${formatTokenAmount(args[2])} units, available ${formatTokenAmount(args[3])} units.`,
        parsed,
        data
      );
    }

    if (name === "InsufficientBalanceForUser") {
      return formatDecodedContractError(
        `Insufficient balance for ${args[0]}: requires ${formatTokenAmount(args[2])} units, available ${formatTokenAmount(args[3])} units.`,
        parsed,
        data
      );
    }

    if (name === "InvalidTransferRecipient") {
      return formatDecodedContractError(
        `Invalid transfer recipient: ${args[0]}. Use a different MarginX account address.`,
        parsed,
        data
      );
    }

    if (name === "InsufficientMargin") {
      return formatDecodedContractError(
        `Insufficient margin for ${args[0]}: available ${formatSignedTokenAmount(args[1])} USDC, required ${formatTokenAmount(args[2])} USDC.`,
        parsed,
        data
      );
    }

    if (name === "InsufficientEquity") {
      // args: [required18 (uint256), equity18 (int256)] — 18-dec USD.
      // The contract also reverts with (0,0) when the risk-engine call fails;
      // avoid the misleading "requires 0 USD" line in that case.
      if (args[0].isZero() && args[1].isZero()) {
        return formatDecodedContractError(
          "Could not verify account equity with the risk engine. Please try again shortly.",
          parsed,
          data
        );
      }
      return formatDecodedContractError(
        `Not enough account equity: requires ${formatTokenAmount(args[0])} USD margin, your equity is ${formatSignedTokenAmount(args[1])} USD. Deposit more collateral or reduce your position size.`,
        parsed,
        data
      );
    }

    if (name === "DepositLockActive") {
      return formatDecodedContractError(
        `Deposit is still locked until block ${args[0].toString()}. Try again after the next block.`,
        parsed,
        data
      );
    }

    if (name === "LeverageExceeded") {
      return formatDecodedContractError(
        `Position leverage exceeds the market limit. Contract reported leverage: ${args[0].toString()}x. Reduce order size, add margin, or lower leverage.`,
        parsed,
        data
      );
    }

    if (name === "EffectiveLeverageExceeded") {
      return formatDecodedContractError(
        `Effective leverage exceeds the limit. Requested ${args[0].toString()}x, max ${args[1].toString()}x.`,
        parsed,
        data
      );
    }

    if (name === "OpenInterestExceeded") {
      return formatDecodedContractError(
        `Open interest limit exceeded. New open interest ${formatTokenAmount(args[0])} USD, max ${formatTokenAmount(args[1])} USD.`,
        parsed,
        data
      );
    }

    if (name === "OpenInterestImbalanceExceeded") {
      return formatDecodedContractError(
        `Open interest imbalance limit exceeded. Imbalance ${formatTokenAmount(args[0])} USD, max ${formatTokenAmount(args[1])} USD.`,
        parsed,
        data
      );
    }

    if (name === "StalePrice") {
      const priceTimestamp = Number(args[1].toString());
      const maxAgeSeconds = args[2].toString();
      const lastUpdate =
        priceTimestamp > 0
          ? new Date(priceTimestamp * 1000).toLocaleString()
          : "never";

      return formatDecodedContractError(
        `Oracle price is stale or missing. Last oracle update: ${lastUpdate}. Max allowed age: ${maxAgeSeconds}s.`,
        parsed,
        data
      );
    }

    if (name === "InvalidPrice") {
      return formatDecodedContractError(
        "Oracle price is missing for this market.",
        parsed,
        data
      );
    }

    const knownMessage = KNOWN_REVERT_SELECTORS[selector];
    if (knownMessage) {
      return formatDecodedContractError(knownMessage, parsed, data);
    }

    return formatDecodedContractError(
      `Contract reverted: ${humanizeErrorName(name)}.`,
      parsed,
      data
    );
  } catch {
    // We parsed the selector but a specific formatter hit an unexpected arg
    // shape — still return a useful decoded message rather than leaking bytes.
    const knownMessage = KNOWN_REVERT_SELECTORS[selector];
    return formatDecodedContractError(
      knownMessage || `Contract reverted: ${humanizeErrorName(parsed.name)}.`,
      parsed,
      data
    );
  }
};
